"use client";

import { useEffect, useState } from "react";

interface SymbolItem {
  symbol: string;
  name: string;
  sector?: string;
}

interface DailyTrade {
  id: string;
  symbol: string;
  quantity: number;
  buy_price: number;
  buy_date: string;
  sell_price?: number;
  sell_date?: string;
  notes?: string;
  moved_to_portfolio?: boolean;
}

interface PriceData {
  [symbol: string]: { close: number; date: string };
}

interface LatestPriceRow {
  symbol?: string;
  close?: number;
  date?: string;
}

const formatNumber = (value: number, digits = 2) =>
  value.toLocaleString(undefined, { maximumFractionDigits: digits, minimumFractionDigits: digits });

const todayISO = () => {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
};

export default function DailyTrackerPage() {
  const [token, setToken] = useState<string | null>(null);
  const [allSymbols, setAllSymbols] = useState<SymbolItem[]>([]);
  const [trades, setTrades] = useState<DailyTrade[]>([]);
  const [priceMap, setPriceMap] = useState<PriceData>({});
  const [loaded, setLoaded] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");

  // Form state
  const [symbolInput, setSymbolInput] = useState("");
  const [filteredSymbols, setFilteredSymbols] = useState<SymbolItem[]>([]);
  const [selectedSymbol, setSelectedSymbol] = useState<SymbolItem | null>(null);
  const [quantity, setQuantity] = useState("");
  const [buyPrice, setBuyPrice] = useState("");
  const [buyDate, setBuyDate] = useState(todayISO());
  const [notes, setNotes] = useState("");

  // Load symbols on mount
  useEffect(() => {
    if (typeof window !== "undefined") {
      setToken(localStorage.getItem("psx_auth_token"));
    }
    const loadSymbols = async () => {
      try {
        const res = await fetch("/api/symbols/list");
        if (!res.ok) throw new Error("Failed to load symbols");
        const json = await res.json();
        setAllSymbols(Array.isArray(json.symbols) ? json.symbols : []);
      } catch (err) {
        console.error("Error loading symbols:", err);
      }
    };
    loadSymbols();
  }, []);

  // Load/save trades from localStorage
  useEffect(() => {
    const loadDailyTrades = async () => {
      try {
        if (!token) return;
        const res = await fetch("/api/daily-trades", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.status === 401 || res.status === 403) {
          if (typeof window !== "undefined") {
            localStorage.removeItem("psx_auth_token");
            localStorage.removeItem("psx_auth_email");
          }
          setToken(null);
          return;
        }
        if (!res.ok) throw new Error("Failed to load daily trades");
        const json = await res.json();
        const data = json?.data;
        if (data?.trades && Array.isArray(data.trades)) {
          setTrades(data.trades);
        }
      } catch (err) {
        console.error("Error loading trades:", err);
      } finally {
        setLoaded(true);
      }
    };
    loadDailyTrades();
  }, [token]);

  useEffect(() => {
    if (!loaded || !token) return;
    const saveDailyTrades = async () => {
      try {
        const symbols = Array.from(new Set(trades.map((t) => t.symbol)));
        const res = await fetch("/api/daily-trades", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ data: { symbols, trades } }),
        });
        if (!res.ok) throw new Error("Failed to save daily trades");
        setSaveStatus("Saved");
        setTimeout(() => setSaveStatus(""), 1500);
      } catch (err) {
        console.error("Error saving trades:", err);
        setSaveStatus("Save failed");
      }
    };
    const timer = setTimeout(saveDailyTrades, 400);
    return () => clearTimeout(timer);
  }, [trades, loaded, token]);

  // Fetch prices for all open trades
  useEffect(() => {
    const openSymbols = new Set(
      trades.filter((t) => !t.sell_price && !t.moved_to_portfolio).map((t) => t.symbol)
    );
    if (openSymbols.size === 0) return;

    const fetchPrices = async () => {
      try {
        const res = await fetch("/api/prices/latest", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ symbols: Array.from(openSymbols) }),
        });
        if (!res.ok) return;
        const data = await res.json();
        if (data.results) {
          const prices: PriceData = {};
          Object.entries(data.results as Record<string, LatestPriceRow>).forEach(([, row]) => {
            if (!row?.symbol) return;
            prices[row.symbol] = {
              close: row.close || 0,
              date: row.date || "",
            };
          });
          setPriceMap(prices);
        }
      } catch (err) {
        console.error("Error fetching prices:", err);
      }
    };

    fetchPrices();
    const interval = setInterval(fetchPrices, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, [trades]);

  // Handle symbol input & filtering
  const handleSymbolInput = (value: string) => {
    setSymbolInput(value.toUpperCase());
    const query = value.trim().toUpperCase();
    if (query.length >= 2) {
      const filtered = allSymbols
        .filter((s) => s.symbol.startsWith(query))
        .slice(0, 10);
      setFilteredSymbols(filtered);
    } else {
      setFilteredSymbols([]);
    }
  };

  // Select symbol from dropdown
  const selectSymbol = (sym: SymbolItem) => {
    setSelectedSymbol(sym);
    setSymbolInput(sym.symbol);
    setFilteredSymbols([]);
    // Load closing price for this symbol
    const lastClose = priceMap[sym.symbol]?.close || 0;
    if (lastClose) {
      setBuyPrice(lastClose.toString());
    }
  };

  // Add new trade
  const handleAddTrade = () => {
    if (!selectedSymbol || !quantity || !buyPrice || !buyDate) {
      alert("Please fill in all fields");
      return;
    }

    const newTrade: DailyTrade = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      symbol: selectedSymbol.symbol,
      quantity: Number(quantity),
      buy_price: Number(buyPrice),
      buy_date: buyDate,
      notes: notes || undefined,
    };

    setTrades((prev) => [...prev, newTrade]);

    // Reset form
    setSymbolInput("");
    setSelectedSymbol(null);
    setQuantity("");
    setBuyPrice("");
    setBuyDate(todayISO());
    setNotes("");
  };

  // Sell a trade
  const handleSell = (tradeId: string, sellPrice: number) => {
    if (sellPrice <= 0) {
      alert("Please enter a valid sell price");
      return;
    }

    setTrades((prev) =>
      prev.map((t) =>
        t.id === tradeId ? { ...t, sell_price: sellPrice, sell_date: todayISO() } : t
      )
    );
  };

  const handleMoveToPortfolio = async (tradeId: string) => {
    const trade = trades.find((t) => t.id === tradeId);
    if (!trade) return;
    if (trade.sell_price) {
      alert("This trade is already sold. Only open trades can be moved to portfolio.");
      return;
    }
    const ok = window.confirm(`Move ${trade.symbol} to portfolio as BUY?`);
    if (!ok) return;
    try {
      const res = await fetch("/api/daily-trades/move", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ trade_id: tradeId }),
      });
      if (!res.ok) throw new Error("Failed to move trade");
      const json = await res.json();
      const updated = json?.daily?.data?.trades;
      if (Array.isArray(updated)) {
        setTrades(updated);
      } else {
        setTrades((prev) =>
          prev.map((t) => (t.id === tradeId ? { ...t, moved_to_portfolio: true } : t))
        );
      }
    } catch (err) {
      console.error(err);
      alert("Failed to move trade to portfolio.");
    }
  };

  // Calculate P&L for a trade
  const calculatePnL = (trade: DailyTrade) => {
    if (!trade.sell_price) return null;

    const grossProfit = (trade.sell_price - trade.buy_price) * trade.quantity;
    
    // Apply capital gains tax only if bought and sold on same day
    let capitalGainsTax = 0;
    if (trade.buy_date === trade.sell_date) {
      // 0.15% applied once for the trade
      capitalGainsTax = Math.abs(grossProfit) * 0.0015;
    }
    
    const netProfit = grossProfit - capitalGainsTax;
    const returnPct = (grossProfit / (trade.buy_price * trade.quantity)) * 100;

    return {
      grossProfit,
      capitalGainsTax,
      netProfit,
      returnPct,
    };
  };

  // Open trades
  const openTrades = trades.filter((t) => !t.sell_price && !t.moved_to_portfolio);
  const closedTrades = trades.filter((t) => t.sell_price && !t.moved_to_portfolio);
  const portfolioTrades = trades.filter((t) => t.moved_to_portfolio);

  if (!token) {
    return (
      <>
        <section className="page-header page-header--accent">
          <div className="page-header-container">
            <h1 className="page-title">Daily Trade Tracker</h1>
            <p className="page-subtitle">Login required</p>
          </div>
        </section>
        <section className="strategy-detail-section">
          <div className="strategy-detail-container">
            <div className="strategy-list-card">
              <div className="strategy-list-header">
                <h2>Login Required</h2>
                <span className="strategy-tag tag-orange">AUTH</span>
              </div>
              <p style={{ color: "var(--color-text-muted)", marginTop: "0.75rem" }}>
                Please login to view and save your daily trades.
              </p>
              <div style={{ marginTop: "1rem" }}>
                <a href="/login" className="navbar-button" style={{ padding: "0.5rem 1rem", display: "inline-block" }}>
                  Go to Login
                </a>
              </div>
            </div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <section className="page-header page-header--accent">
        <div className="page-header-container">
          <h1 className="page-title">Daily Trade Tracker</h1>
          <p className="page-subtitle">Track and manage your daily trades</p>
          {saveStatus && (
            <div style={{ marginTop: "0.5rem", color: "var(--color-text-muted)", fontSize: "0.85rem" }}>
              {saveStatus}
            </div>
          )}
        </div>
      </section>

      <section className="strategy-detail-section">
        <div className="strategy-detail-container">
          {/* Add Trade Form */}
          <div
            style={{
              background: "rgba(30, 41, 59, 0.4)",
              border: "1px solid rgba(148, 163, 184, 0.2)",
              borderRadius: "12px",
              padding: "1.5rem",
              marginBottom: "2rem",
            }}
          >
            <h2 style={{ margin: "0 0 1.5rem 0", fontSize: "1.1rem", color: "var(--color-text)", fontWeight: 600 }}>
              ➕ Add New Trade
            </h2>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1rem", marginBottom: "1rem" }}>
              {/* Symbol Input with Autocomplete */}
              <div style={{ position: "relative" }}>
                <label style={{ display: "block", marginBottom: "0.5rem", color: "var(--color-text-muted)", fontSize: "0.85rem", fontWeight: 600 }}>
                  Stock Symbol
                </label>
                <input
                  type="text"
                  placeholder="Type symbol (e.g., PO)"
                  value={symbolInput}
                  onChange={(e) => handleSymbolInput(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "0.6rem",
                    border: "1px solid rgba(148, 163, 184, 0.3)",
                    borderRadius: "6px",
                    background: "rgba(30, 41, 59, 0.5)",
                    color: "var(--color-text)",
                    fontSize: "0.9rem",
                  }}
                />
                {filteredSymbols.length > 0 && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      background: "rgba(15, 23, 32, 0.95)",
                      border: "1px solid rgba(148, 163, 184, 0.3)",
                      borderRadius: "6px",
                      marginTop: "0.25rem",
                      maxHeight: "200px",
                      overflowY: "auto",
                      zIndex: 10,
                    }}
                  >
                    {filteredSymbols.map((sym) => (
                      <div
                        key={sym.symbol}
                        onClick={() => selectSymbol(sym)}
                        style={{
                          padding: "0.6rem",
                          cursor: "pointer",
                          borderBottom: "1px solid rgba(148, 163, 184, 0.1)",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "rgba(59, 130, 246, 0.2)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "transparent";
                        }}
                      >
                        <div style={{ fontWeight: 600, color: "var(--color-text)" }}>{sym.symbol}</div>
                        <div style={{ fontSize: "0.8rem", color: "var(--color-text-muted)" }}>{sym.name}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Quantity */}
              <div>
                <label style={{ display: "block", marginBottom: "0.5rem", color: "var(--color-text-muted)", fontSize: "0.85rem", fontWeight: 600 }}>
                  Quantity
                </label>
                <input
                  type="number"
                  placeholder="100"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "0.6rem",
                    border: "1px solid rgba(148, 163, 184, 0.3)",
                    borderRadius: "6px",
                    background: "rgba(30, 41, 59, 0.5)",
                    color: "var(--color-text)",
                    fontSize: "0.9rem",
                  }}
                />
              </div>

              {/* Buy Price */}
              <div>
                <label style={{ display: "block", marginBottom: "0.5rem", color: "var(--color-text-muted)", fontSize: "0.85rem", fontWeight: 600 }}>
                  Buy Price
                </label>
                <input
                  type="number"
                  placeholder="100.50"
                  value={buyPrice}
                  onChange={(e) => setBuyPrice(e.target.value)}
                  step="0.01"
                  style={{
                    width: "100%",
                    padding: "0.6rem",
                    border: "1px solid rgba(148, 163, 184, 0.3)",
                    borderRadius: "6px",
                    background: "rgba(30, 41, 59, 0.5)",
                    color: "var(--color-text)",
                    fontSize: "0.9rem",
                  }}
                />
              </div>

              {/* Buy Date */}
              <div>
                <label style={{ display: "block", marginBottom: "0.5rem", color: "var(--color-text-muted)", fontSize: "0.85rem", fontWeight: 600 }}>
                  Buy Date
                </label>
                <input
                  type="date"
                  value={buyDate}
                  onChange={(e) => setBuyDate(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "0.6rem",
                    border: "1px solid rgba(148, 163, 184, 0.3)",
                    borderRadius: "6px",
                    background: "rgba(30, 41, 59, 0.5)",
                    color: "var(--color-text)",
                    fontSize: "0.9rem",
                  }}
                />
              </div>

              {/* Notes */}
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={{ display: "block", marginBottom: "0.5rem", color: "var(--color-text-muted)", fontSize: "0.85rem", fontWeight: 600 }}>
                  Notes (optional)
                </label>
                <input
                  type="text"
                  placeholder="E.g., Good reversal signal"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "0.6rem",
                    border: "1px solid rgba(148, 163, 184, 0.3)",
                    borderRadius: "6px",
                    background: "rgba(30, 41, 59, 0.5)",
                    color: "var(--color-text)",
                    fontSize: "0.9rem",
                  }}
                />
              </div>
            </div>

            <button
              onClick={handleAddTrade}
              style={{
                padding: "0.7rem 1.5rem",
                background: "linear-gradient(135deg, #0EA5E9 0%, #06B6D4 100%)",
                color: "white",
                border: "none",
                borderRadius: "6px",
                fontWeight: 600,
                cursor: "pointer",
                fontSize: "0.95rem",
              }}
            >
              Add Trade
            </button>
          </div>

          {/* Open Trades */}
          {openTrades.length > 0 && (
            <div style={{ marginBottom: "2rem" }}>
              <h3 style={{ marginBottom: "1rem", fontSize: "1.1rem", color: "var(--color-text)", fontWeight: 600 }}>
                📊 Open Trades
              </h3>
              <div style={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    background: "rgba(30, 41, 59, 0.2)",
                    borderRadius: "8px",
                    overflow: "hidden",
                  }}
                >
                  <thead>
                    <tr style={{ borderBottom: "1px solid rgba(148, 163, 184, 0.2)" }}>
                      <th style={{ padding: "1rem", textAlign: "left", fontWeight: 600, color: "var(--color-text-muted)" }}>Symbol</th>
                      <th style={{ padding: "1rem", textAlign: "left", fontWeight: 600, color: "var(--color-text-muted)" }}>Qty</th>
                      <th style={{ padding: "1rem", textAlign: "left", fontWeight: 600, color: "var(--color-text-muted)" }}>Buy Price</th>
                      <th style={{ padding: "1rem", textAlign: "left", fontWeight: 600, color: "var(--color-text-muted)" }}>Current</th>
                      <th style={{ padding: "1rem", textAlign: "left", fontWeight: 600, color: "var(--color-text-muted)" }}>Unrealized P&L</th>
                      <th style={{ padding: "1rem", textAlign: "left", fontWeight: 600, color: "var(--color-text-muted)" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {openTrades.map((trade) => {
                      const currentPrice = priceMap[trade.symbol]?.close || 0;
                      const unrealizedPnL = currentPrice ? (currentPrice - trade.buy_price) * trade.quantity : 0;
                      const unrealizedReturn = currentPrice ? ((currentPrice - trade.buy_price) / trade.buy_price) * 100 : 0;

                      return (
                        <tr key={trade.id} style={{ borderBottom: "1px solid rgba(148, 163, 184, 0.1)" }}>
                          <td style={{ padding: "1rem", fontWeight: 600, color: "var(--color-text)" }}>{trade.symbol}</td>
                          <td style={{ padding: "1rem", color: "var(--color-text)" }}>{trade.quantity}</td>
                          <td style={{ padding: "1rem", color: "var(--color-text)" }}>{formatNumber(trade.buy_price)}</td>
                          <td style={{ padding: "1rem", color: "var(--color-text)" }}>{currentPrice > 0 ? formatNumber(currentPrice) : "—"}</td>
                          <td
                            style={{
                              padding: "1rem",
                              color: unrealizedPnL > 0 ? "var(--color-success)" : unrealizedPnL < 0 ? "var(--color-danger)" : "var(--color-text)",
                              fontWeight: 600,
                            }}
                          >
                            {currentPrice > 0 ? `${formatNumber(unrealizedPnL)} (${unrealizedReturn.toFixed(2)}%)` : "—"}
                          </td>
                          <td style={{ padding: "1rem" }}>
                            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                              <SellButton trade={trade} onSell={handleSell} />
                              <button
                                onClick={() => handleMoveToPortfolio(trade.id)}
                                style={{
                                  padding: "0.4rem 0.8rem",
                                  background: "rgba(56, 189, 248, 0.15)",
                                  color: "#38bdf8",
                                  border: "1px solid rgba(56, 189, 248, 0.6)",
                                  borderRadius: "4px",
                                  fontWeight: 600,
                                  cursor: "pointer",
                                  fontSize: "0.8rem",
                                }}
                              >
                                Move to Portfolio
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Closed Trades (Daily P&L) */}
          {closedTrades.length > 0 && (
            <div style={{ marginBottom: "2rem" }}>
              <h3 style={{ marginBottom: "1rem", fontSize: "1.1rem", color: "var(--color-text)", fontWeight: 600 }}>
                ✅ Closed Trades
              </h3>
              <div style={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    background: "rgba(30, 41, 59, 0.2)",
                    borderRadius: "8px",
                    overflow: "hidden",
                  }}
                >
                  <thead>
                    <tr style={{ borderBottom: "1px solid rgba(148, 163, 184, 0.2)" }}>
                      <th style={{ padding: "1rem", textAlign: "left", fontWeight: 600, color: "var(--color-text-muted)" }}>Symbol</th>
                      <th style={{ padding: "1rem", textAlign: "left", fontWeight: 600, color: "var(--color-text-muted)" }}>Qty</th>
                      <th style={{ padding: "1rem", textAlign: "left", fontWeight: 600, color: "var(--color-text-muted)" }}>Buy Price</th>
                      <th style={{ padding: "1rem", textAlign: "left", fontWeight: 600, color: "var(--color-text-muted)" }}>Sell Price</th>
                      <th style={{ padding: "1rem", textAlign: "left", fontWeight: 600, color: "var(--color-text-muted)" }}>Gross P&L</th>
                      <th style={{ padding: "1rem", textAlign: "left", fontWeight: 600, color: "var(--color-text-muted)" }}>Tax</th>
                      <th style={{ padding: "1rem", textAlign: "left", fontWeight: 600, color: "var(--color-text-muted)" }}>Net P&L</th>
                      <th style={{ padding: "1rem", textAlign: "left", fontWeight: 600, color: "var(--color-text-muted)" }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {closedTrades.map((trade) => {
                      const pnl = calculatePnL(trade);
                      if (!pnl) return null;

                      return (
                        <tr key={trade.id} style={{ borderBottom: "1px solid rgba(148, 163, 184, 0.1)" }}>
                          <td style={{ padding: "1rem", fontWeight: 600, color: "var(--color-text)" }}>{trade.symbol}</td>
                          <td style={{ padding: "1rem", color: "var(--color-text)" }}>{trade.quantity}</td>
                          <td style={{ padding: "1rem", color: "var(--color-text)" }}>{formatNumber(trade.buy_price)}</td>
                          <td style={{ padding: "1rem", color: "var(--color-text)" }}>{formatNumber(trade.sell_price!)}</td>
                          <td
                            style={{
                              padding: "1rem",
                              color: pnl.grossProfit > 0 ? "var(--color-success)" : "var(--color-danger)",
                              fontWeight: 600,
                            }}
                          >
                            {formatNumber(pnl.grossProfit)}
                          </td>
                          <td style={{ padding: "1rem", color: "var(--color-text-muted)" }}>
                            {pnl.capitalGainsTax > 0 ? formatNumber(pnl.capitalGainsTax) : "—"}
                          </td>
                          <td
                            style={{
                              padding: "1rem",
                              color: pnl.netProfit > 0 ? "var(--color-success)" : pnl.netProfit < 0 ? "var(--color-danger)" : "var(--color-text)",
                              fontWeight: 600,
                            }}
                          >
                            {formatNumber(pnl.netProfit)} ({pnl.returnPct.toFixed(2)}%)
                          </td>
                          <td style={{ padding: "1rem", color: "var(--color-text-muted)", fontWeight: 600 }}>
                            Closed
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Summary */}
          {closedTrades.length > 0 && (
            <div
              style={{
                background: "rgba(30, 41, 59, 0.4)",
                border: "1px solid rgba(148, 163, 184, 0.2)",
                borderRadius: "12px",
                padding: "1.5rem",
              }}
            >
              <h4 style={{ margin: "0 0 1rem 0", color: "var(--color-text)", fontWeight: 600 }}>
                📈 Today&apos;s Summary
              </h4>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                  gap: "1rem",
                }}
              >
                <div>
                  <div style={{ color: "var(--color-text-muted)", fontSize: "0.85rem" }}>Total Trades</div>
                  <div style={{ fontSize: "1.5rem", fontWeight: 600, color: "var(--color-text)" }}>
                    {closedTrades.length}
                  </div>
                </div>
                <div>
                  <div style={{ color: "var(--color-text-muted)", fontSize: "0.85rem" }}>Total P&L</div>
                  <div
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: 600,
                      color: closedTrades.reduce((sum, t) => sum + (calculatePnL(t)?.netProfit || 0), 0) > 0 ? "var(--color-success)" : "var(--color-danger)",
                    }}
                  >
                    {formatNumber(closedTrades.reduce((sum, t) => sum + (calculatePnL(t)?.netProfit || 0), 0))}
                  </div>
                </div>
                <div>
                  <div style={{ color: "var(--color-text-muted)", fontSize: "0.85rem" }}>Total Tax</div>
                  <div style={{ fontSize: "1.5rem", fontWeight: 600, color: "var(--color-text)" }}>
                    -{formatNumber(closedTrades.reduce((sum, t) => sum + (calculatePnL(t)?.capitalGainsTax || 0), 0))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {trades.length === 0 && (
            <div
              style={{
                textAlign: "center",
                padding: "3rem",
                color: "var(--color-text-muted)",
                background: "rgba(30, 41, 59, 0.3)",
                borderRadius: "8px",
                border: "1px solid rgba(148, 163, 184, 0.1)",
              }}
            >
              <p style={{ fontSize: "1.1rem", marginBottom: "0.5rem" }}>No trades yet</p>
              <p style={{ fontSize: "0.9rem", opacity: 0.8 }}>Add your first trade above to start tracking</p>
            </div>
          )}

          {portfolioTrades.length > 0 && (
            <div style={{ marginTop: "2rem" }}>
              <h3 style={{ marginBottom: "1rem", fontSize: "1.1rem", color: "var(--color-text)", fontWeight: 600 }}>
                📦 Moved to Portfolio
              </h3>
              <div style={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    background: "rgba(30, 41, 59, 0.2)",
                    borderRadius: "8px",
                    overflow: "hidden",
                  }}
                >
                  <thead>
                    <tr style={{ borderBottom: "1px solid rgba(148, 163, 184, 0.2)" }}>
                      <th style={{ padding: "1rem", textAlign: "left", fontWeight: 600, color: "var(--color-text-muted)" }}>Symbol</th>
                      <th style={{ padding: "1rem", textAlign: "left", fontWeight: 600, color: "var(--color-text-muted)" }}>Qty</th>
                      <th style={{ padding: "1rem", textAlign: "left", fontWeight: 600, color: "var(--color-text-muted)" }}>Buy Price</th>
                      <th style={{ padding: "1rem", textAlign: "left", fontWeight: 600, color: "var(--color-text-muted)" }}>Buy Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {portfolioTrades.map((trade) => (
                      <tr key={trade.id} style={{ borderBottom: "1px solid rgba(148, 163, 184, 0.1)" }}>
                        <td style={{ padding: "1rem", fontWeight: 600, color: "var(--color-text)" }}>{trade.symbol}</td>
                        <td style={{ padding: "1rem", color: "var(--color-text)" }}>{trade.quantity}</td>
                        <td style={{ padding: "1rem", color: "var(--color-text)" }}>{formatNumber(trade.buy_price)}</td>
                        <td style={{ padding: "1rem", color: "var(--color-text)" }}>{trade.buy_date}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}

function SellButton({ trade, onSell }: { trade: DailyTrade; onSell: (id: string, price: number) => void }) {
  const [sellPrice, setSellPrice] = useState("");
  const [showInput, setShowInput] = useState(false);

  if (showInput) {
    return (
      <div style={{ display: "flex", gap: "0.5rem" }}>
        <input
          type="number"
          placeholder="Sell price"
          value={sellPrice}
          onChange={(e) => setSellPrice(e.target.value)}
          step="0.01"
          style={{
            padding: "0.4rem",
            border: "1px solid rgba(148, 163, 184, 0.3)",
            borderRadius: "4px",
            background: "rgba(30, 41, 59, 0.5)",
            color: "var(--color-text)",
            fontSize: "0.8rem",
            width: "100px",
          }}
        />
        <button
          onClick={() => {
            onSell(trade.id, Number(sellPrice));
            setSellPrice("");
            setShowInput(false);
          }}
          style={{
            padding: "0.4rem 0.8rem",
            background: "var(--color-success)",
            color: "white",
            border: "none",
            borderRadius: "4px",
            fontWeight: 600,
            cursor: "pointer",
            fontSize: "0.8rem",
          }}
        >
          Sell
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={() => setShowInput(true)}
      style={{
        padding: "0.4rem 0.8rem",
        background: "rgba(16, 185, 129, 0.2)",
        color: "var(--color-success)",
        border: "1px solid var(--color-success)",
        borderRadius: "4px",
        fontWeight: 600,
        cursor: "pointer",
        fontSize: "0.8rem",
      }}
    >
      Sell
    </button>
  );
}
